import * as React from 'react'
import { useState } from 'react'
import { GetStaticProps } from 'next'
import { NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import useInView from 'react-cool-inview'
import { Link, Element } from 'react-scroll'
import Image from 'next/image'
import { motion } from 'framer-motion'

import Container from '~/components/Container'
import BuyButton from '~/components/BuyButton'
import ProductDetails from '~/components/ProductDetails'
import NotchedCard from '~/components/NotchedCard'
import CircledPlus from '~/components/icons/IconCircledPlus'
import ImageGallery from '~/components/ImageGallery'
import Features from '~/components/Features'
import AppCTAs from '~/components/AppCTAs'

const title = 'Model Z - Mary AG'
const description =
  'Mary Model Z, the first and only grow box created for your living room. Smart. Automated. Effortless. Model Z is the automated home grow box that takes the guesswork out of growing. The uncompromising and compact design empowers anyone to grow what they want, when they want, and where they want.'

export const getStaticProps: GetStaticProps = async () => {
  const data = {
    tech_specs: {
      model: 'Model Z',
      colour: 'Space Grey (Matte Black)',
      external_leds: '16.7 million colour options via mobile app',
      power_consumption: '20-300 Watts, approx. $15/month',
      size_and_weight: [
        { height: '135cm (53.1 inches)' },
        { width: '45cm (17.7 inches)' },
        { depth: '33cm (12.9 inches)' },
        { weight: '25kg (55.1 pounds)' }
      ],
      grow_leds: 'Multidirection with high efficiency agricultural grade LEDs',
      processor: 'ARM Cortex M3, 120MHz',
      app: 'iOS & Android',
      climate_control: 'Built-in air conditioning',
      electrical_and_operating_requirements: [
        { line_voltage: '100-240V AC' },
        {
          operating_temperature:
            '10\u00B0 to 30\u00B0 C (50\u00B0 to 86\u00B0 F)'
        },
        { relative_humidity: '5% to 95% Noncondensing' }
      ],
      connectivity: '802.11/b/g/n Wi-Fi',
      certifications: 'FCC, ETL, IC, EMC'
    },
    features: [
      {
        title: 'In App Support',
        description:
          'The Mary AG app will guide you through every growth phase. Need help? Connect with our experienced team of Growers. '
      },
      {
        title: 'Wifi Enabled',
        description:
          'Check your plant size, water level, and growing conditions from anywhere in the world, at any time. '
      },
      {
        title: 'Smart Climate Assistance',
        description:
          'Climate is automatically adjusted to mimic normal warming and cooling cycles found in nature, as well as to best suit your home environment.'
      },
      {
        title: 'Real Time Monitoring',
        description:
          'Grow chamber conditions are continuously monitored. Alerts and reminders are provided to help with plant care. '
      },
      {
        title: 'Growth Phase Optimization',
        description:
          'Optimal growing conditions are applied for each stage of growth in the plant life cycle to maximize yield. '
      }
    ],
    external_features: [
      {
        title: 'Immersive, Intracanopy Lighting'
      },
      {
        title: 'Dual E-glass Doors'
      },
      {
        title: 'Fully Monitored Grow Environment'
      },
      {
        title: 'Carbon Odour Filter'
      }
    ],
    image_gallery: [
      { src: '/images/gallery-image-1.png' },
      { src: '/images/gallery-image-2.png' },
      { src: '/images/gallery-image-3.png' },
      { src: '/images/gallery-image-4.png' },
      { src: '/images/gallery-image-5.png' },
      { src: '/images/gallery-image-6.png' }
    ],
    technology_section: {
      src: '/images/product-page-tech.png',
      section_title: 'Smart Grow Box Technology, Fully Automated',
      description_title: 'Technology That learns',
      body: 'Each Model Z grow box is connected to the Mary AG software platform which is used to run automation and analyze data. The data is then used to improve the software so that your smart grow box gets smarter with every plant you grow.',
      sub_body:
        'With a simple push of a button, the Mary Model Z automatically mimics the sunrise and sunset in order to create optimal growing conditions for your plant. Immersive lighting bathes the plant in light for better yields in every grow cycle.'
    },
    design_section: [
      {
        title: 'Grow What You Want, Where You Want',
        src: '/images/design-section-image-1.png'
      },
      {
        sub_title: 'Growing for all experience levels, anywhere in the world.',
        body: 'We have always grown at nature’s mercy. The best growing conditions have always required an ideal climate and access to fertile land. Now, in the climate controlled grow chamber, you can grow virtually any cultivar, regardless of season. Keep your kush or tomatoes thriving all year long.',
        sub_body:
          'Whether this is your first time growing at home, or you are an experienced greenthumb, the Mary Model Z will make your life easier. You will receive tips and be guided through the grow cycle all while the Model Z delivers optimal growing conditions for each phase of plant life. If you want to experiment and tweak the conditions, simply take control through manual settings in the Mary AG app.',
        src: '/images/design-section-image-2.png'
      },
      {
        title: 'Use Less Land, And Less Water',
        sub_title: 'Designed for Any Living Space',
        body: 'The Model Z is designed with efficiency in mind. An immersive multidirectional LED light source provides full light to the entire plant while using less power than most of your home appliances.',
        sub_body:
          'Unlike traditional growing methods that only use top-down lighting, the Model Z maximizes the amount you can harvest from each plant while remaining compact.',
        src: '/images/design-section-image-3.png'
      },
      {
        title: 'Zero Pesticides, for a Cleaner Harvest',
        sub_title: 'Healthy, Predictable Results',
        body: 'The built in water tank allows soilless, hydroponic growing that is low maintenance and more resistant to pests.',
        sub_body:
          'Paired with the Mary AG’s pre-measured nutrients, you can ensure that your harvest is healthy and pesticide-free. You can also choose to use your own nutrients to take control of what goes into your plant.',
        src_1: '/images/design-section-image-4.png',
        src_2: '/images/design-section-image-5.png'
      }
    ],
    app_features: [
      {
        title: 'Growing Advice',
        description:
          'Our best growing tips in one place! Our advice is designed to help you get the most from your growing experience. '
      },
      {
        title: 'Track Your Growth',
        description:
          'Designed to eliminate the guesswork and give you access to the information you need to track your grow in real-time. '
      },
      {
        title: 'Live Support',
        description:
          'Have a question? Our app offers live support to those who need help during the life cycle of their grow. '
      }
    ]
  }
  return { props: { data } }
}

const ARButton = dynamic(() => import('~/components/ARButton'), { ssr: false })

export default function ModelZPage(props) {
  const handleTitleScroll = (position) => {
    document
      .querySelector('#sectionTitles')
      .scrollTo({ left: position, behavior: 'smooth' })
  }

  const { observe: observeGrowBoxSection, inView: inViewGrowBoxSection } =
    useInView<HTMLDivElement>({
      threshold: 0.25,
      onEnter: () => {
        handleTitleScroll(0)
      }
    })
  const { observe: observeTechnologySection, inView: inViewTechnologySection } =
    useInView<HTMLDivElement>({
      threshold: 0.25,
      onEnter: () => {
        handleTitleScroll(100)
      }
    })

  const { observe: observeDesignSection, inView: inViewDesignSection } =
    useInView<HTMLDivElement>({
      threshold: 0.25,
      onEnter: () => {
        handleTitleScroll(210)
      }
    })
  const { observe: observeTheAppSection, inView: inViewTheAppSection } =
    useInView<HTMLDivElement>({
      threshold: 0.25,
      onEnter: () => {
        handleTitleScroll(280)
      }
    })
  const { observe: observeSpecsSection, inView: inViewSpecsSection } =
    useInView<HTMLDivElement>({
      threshold: 0.25,
      onEnter: () => {
        handleTitleScroll(360)
      }
    })

  const [viewInside, setViewInside] = useState(false)
  const [viewSwapTransition, setViewSwapTransition] = useState(false)

  const handleViewSwap = () => {
    setViewSwapTransition(true)
    setTimeout(() => {
      setViewInside(!viewInside)
      setViewSwapTransition(false)
    }, 500)
  }

  const [viewSwapButtonHover, setViewSwapButtonHover] = useState(false)

  return (
    <Container className="flex flex-col items-center bg-grey-700">
      <NextSeo title={title} description={description} />
      <div className="w-full bg-black px-6 top-[81px] md:top-[89px] sticky border-b border-white border-opacity-10 z-30">
        <div
          title="header"
          className="top-16 max-w-7xl mx-auto w-full flex flex-row justify-between items-center py-6"
        >
          <div
            id="sectionTitles"
            className="flex flex-row whitespace-nowrap overflow-x-scroll pr-44"
          >
            <Link
              className={`pr-3.5 lg:pr-12 text-white cursor-pointer ${
                inViewGrowBoxSection ? '' : 'text-opacity-70'
              }`}
              to="growBoxAnchor"
              smooth={true}
            >
              Grow Box
            </Link>
            <Link
              className={`pr-3.5 lg:pr-12 text-white cursor-pointer ${
                inViewTechnologySection ? '' : 'text-opacity-70'
              }`}
              to="technologyAnchor"
              smooth={true}
            >
              Technology
            </Link>
            <Link
              className={`pr-3.5 lg:pr-12 text-white cursor-pointer ${
                inViewDesignSection ? '' : 'text-opacity-70'
              }`}
              to="designAnchor"
              smooth={true}
            >
              Design
            </Link>
            <Link
              className={`pr-3.5 lg:pr-12 text-white cursor-pointer ${
                inViewTheAppSection ? '' : 'text-opacity-70'
              }`}
              to="theAppAnchor"
              smooth={true}
            >
              The App
            </Link>
            <Link
              className={`text-white pr-20 cursor-pointer ${
                inViewSpecsSection ? '' : 'text-opacity-70'
              }`}
              to="specsAnchor"
              smooth={true}
            >
              Specs
            </Link>
          </div>
          <style jsx>{`
            #sectionTitles::-webkit-scrollbar {
              display: none;
            }
            #sectionTitles {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
          `}</style>
          <div className="flex flex-row relative bg-transparent">
            <span className="pointer-events-none absolute top-0 bottom-0 left-0 w-28 -ml-28 sm:w-64 sm:-ml-64 bg-gradient-to-l from-black"></span>
            <ARButton />
            {/* <div className="pl-5">
              <BuyButton
                className="bg-pink-800 rounded-full hidden lg:block px-4 py-2.5 text-sm text-grey-700 whitespace-nowrap"
                buttonText="Buy - "
                showPrice={true}
              />
              <BuyButton
                className="bg-pink-800 rounded-full lg:hidden px-4 py-3 h-full text-sm text-grey-700 leading-none"
                buttonText="Buy"
                showPrice={false}
              />
            </div> */}
          </div>
        </div>
      </div>
      <section className="bg-grey-700 relative w-full mx-auto pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto lg:px-28">
          <h1 className="text-2xl lg:text-4xl text-white w-full text-center font-medium px-6 pt-16 pb-4 lg:pt-24 lg:pb-12">
            Mary Model Z
          </h1>
          <div className="w-full ">
            <Image
              src="/images/grow-box-condo-toronto.jpeg"
              width="100%"
              height="66.66%"
              objectFit="contain"
              layout="responsive"
              alt="Model Z - Condo Toronto"
            />
          </div>
        </div>
      </section>
      <section
        className="bg-grey-700 relative w-full px-6 lg:px-28 max-w-7xl mx-auto"
        ref={observeGrowBoxSection}
      >
        <Element name="growBoxAnchor" className="absolute -mt-44"></Element>
        <h2 className="font-medium text-xl lg:text-3xl text-white w-4/5">
          The Future of Home Growing Luxury
        </h2>
        <div className="flex flex-col lg:flex-row pt-8 lg:pt-20">
          <div className="flex-1">
            <div className="md:w-3/4 text-white">
              <h2 className="text-base">The Smart Grow Box</h2>
              <p className="pt-11 xl:pt-16 opacity-70 leading-8">
                Growing quality, high-yield plants in the comfort of your own
                home has never been easier.
              </p>
            </div>
            <div className="pt-7 lg:pt-20 md:w-3/4 text-white">
              {props.data.external_features.map((feature, index) => {
                return (
                  <div
                    key={index}
                    className="text-base pb-12 flex flex-col lg:flex-row"
                  >
                    <span className="text-white opacity-70">0{index + 1}</span>
                    <a className="text-lg xl:text-xl lg:pl-5">
                      {feature.title}
                    </a>
                  </div>
                )
              })}
            </div>
          </div>
          <div className="flex-1 relative pt-12 lg:pt-0 -mx-6">
            <NotchedCard notchPosition="top-left" aspectW={100} aspectH={133}>
              <motion.div
                variants={{
                  transition: { opacity: 0.2, transition: { duration: 0.5 } },
                  show: { opacity: 1, transition: { duration: 1 } }
                }}
                animate={viewSwapTransition ? 'transition' : 'show'}
              >
                <Image
                  src={
                    viewInside
                      ? '/images/grow-box-view-inside.png'
                      : '/images/grow-box-view-outside.png'
                  }
                  width="100%"
                  height="133%"
                  objectFit="contain"
                  layout="responsive"
                  alt="View Outside"
                  className=""
                />
              </motion.div>
            </NotchedCard>
            <div
              className="absolute bottom-0 right-0 w-3/4 h-1/6 lg:-bottom-28 lg:-left-18 lg:w-1/2 lg:h-1/2 lg:z-10 cursor-pointer"
              onClick={handleViewSwap}
              onPointerEnter={() => setViewSwapButtonHover(true)}
              onPointerLeave={() => setViewSwapButtonHover(false)}
            >
              <motion.div className="grid grid-rows-1 grid-cols-7 lg:block lg:relative h-full w-full">
                <div className="hidden lg:block overflow-hidden relative">
                  <div className="absolute inset-0 bg-black pointer-events-none opacity-50 z-10" />
                  <motion.div
                    className="hidden lg:block"
                    animate={
                      viewSwapButtonHover ? { scale: 1.3 } : { scale: 1 }
                    }
                    transition={{ duration: 0.45 }}
                  >
                    <Image
                      src={
                        viewInside
                          ? '/images/grow-box-view-outside.png'
                          : '/images/grow-box-view-inside.png'
                      }
                      width="100%"
                      height="133%"
                      objectFit="cover"
                      layout="responsive"
                      alt="View Inside"
                    />
                  </motion.div>
                </div>
                <div className="lg:hidden overflow-hidden col-span-3 h-full relative">
                  <div className="absolute inset-0 bg-black pointer-events-none opacity-50 z-10" />
                  <motion.div
                    animate={
                      viewSwapButtonHover ? { scale: 1.3 } : { scale: 1 }
                    }
                    transition={{ duration: 0.45 }}
                  >
                    <motion.div
                      variants={{
                        transition: {
                          opacity: 0.8,
                          transition: { duration: 0.5 }
                        },
                        show: { opacity: 1, transition: { duration: 1 } }
                      }}
                      animate={viewSwapTransition ? 'transition' : 'show'}
                    >
                      <Image
                        src={
                          viewInside
                            ? '/images/grow-box-view-outside-sm.png'
                            : '/images/grow-box-view-inside-sm.png'
                        }
                        width={184}
                        height={140}
                        objectFit="cover"
                        layout="responsive"
                        alt="View Inside"
                      />
                    </motion.div>
                  </motion.div>
                </div>
                <div className="z-20 col-span-4 lg:absolute lg:top-1/2 lg:left-1/2 lg:transform lg:-translate-y-1/2 lg:-translate-x-1/2 bg-grey-700 lg:bg-transparent pt-2">
                  <div className="flex flex-col justify-center items-center h-full">
                    <CircledPlus
                      className={`h-8 w-8 md:w-12 md:h-12 xl:w-16 xl:h-16 rounded-full ${
                        viewSwapButtonHover ? 'bg-pink-600' : ''
                      }`}
                      hover={viewSwapButtonHover}
                    />
                    <a className="whitespace-nowrap text-sm sm:pt-2 md:pt-4 text-white text-center">
                      {viewInside ? 'View Outside' : 'View Inside'}
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      <section
        className="bg-grey-700 relative w-full pt-16 lg:pt-72 max-w-7xl mx-auto px-6 lg:px-0"
        ref={observeTechnologySection}
      >
        <Element name="technologyAnchor" className="absolute -mt-44"></Element>
        <div className="flex flex-col">
          <div className="w-3/5 z-10">
            <h2 className="text-white font-medium text-xl sm:text-2xl md:text-3xl xl:text-4xl w-4/5 lg:w-3/5 mx-auto -mb-12 sm:-mb-24 md:-mb-28 lg:-mb-28">
              {props.data.technology_section.section_title}
            </h2>
          </div>
          <div className="flex flex-col lg:flex-row">
            <div className="lg:w-3/5 lg:pr-14 -mx-6 lg:-mx-0">
              <NotchedCard
                notchPosition="top-right"
                aspectW={133}
                aspectH={100}
              >
                <Image
                  src={props.data.technology_section.src}
                  alt="MaryAG Tech"
                  height={100}
                  width={133}
                  layout="responsive"
                  objectFit="cover"
                  className="aspect-w-4 aspect-h-3"
                />
              </NotchedCard>
            </div>
            <div className="flex flex-col w-4/5 lg:w-2/5 lg:pl-14 lg:pr-28">
              <h3 className="text-white text-base lg:text-sm xl:text-base md:pb-8 xl:pb-16 pt-16 lg:pt-0">
                {props.data.technology_section.description_title}
              </h3>
              <p className="text-white text-lg lg:text-base xl:text-lg md:pb-6 xl:pb-12 pt-16 lg:pt-0">
                {props.data.technology_section.body}
              </p>
              <p className="text-white opacity-70 text-base lg:text-sm xl:text-base pt-14 lg:pt-0 pb-11 lg:pb-0">
                {props.data.technology_section.sub_body}
              </p>
            </div>
          </div>
        </div>
        <div className="-mx-6 lg:-mx-0 pt-12 lg:pt-40 lg:px-28 pb-24 lg:pb-52">
          <Features features={props.data.features} type="modelZ" />
        </div>
      </section>
      <section
        className="bg-grey-700 relative w-full max-w-7xl"
        ref={observeDesignSection}
      >
        <Element name="designAnchor" className="absolute -mt-44"></Element>
        <div className="flex flex-col pb-16 lg:pb-36">
          <h2 className="font-medium text-white text-xl lg:text-4xl w-4/5 pl-6 lg:pl-28 z-10">
            {props.data.design_section[0].title}
          </h2>
          <div className="w-full lg:w-3/5 lg:self-end lg:pr-28 -mt-4 lg:-mt-44">
            <Image
              src={props.data.design_section[0].src}
              alt="MaryAG Grow What You Want"
              width={500}
              height={375}
              layout="responsive"
              objectFit="cover"
            />
          </div>
        </div>
        <div className="flex flex-col lg:flex-row px-6 lg:px-28">
          <div className="flex-1 lg:order-1 order-2 -mx-6 lg:-mx-0">
            <NotchedCard notchPosition="top-right" aspectW={500} aspectH={667}>
              <Image
                src={props.data.design_section[1].src}
                alt="MaryAG Growing Community"
                width={500}
                height={667}
                layout="responsive"
                objectFit="cover"
              />
            </NotchedCard>
          </div>
          <div className="flex-1 lg:order-2 order-1 flex flex-col lg:pl-28">
            <h3 className="text-white text-base pb-14 lg:pb-16 capitalize">
              {props.data.design_section[1].sub_title}
            </h3>
            <p className="text-white text-lg pb-12">
              {props.data.design_section[1].body}
            </p>
            <p className="text-white opacity-70 text-base pb-12 lg:pb-0">
              {props.data.design_section[1].sub_body}
            </p>
          </div>
        </div>
        <div className="flex flex-col pt-36 lg:pt-52 px-6 lg:px-28">
          <div className="-mx-6 lg:-ml-28">
            <Image
              src={props.data.design_section[2].src}
              alt="MaryAG Use Less Land And Less Water"
              width={500}
              height={300}
              layout="responsive"
              objectFit="cover"
            />
          </div>
          <div className="flex flex-col -mt-8 lg:-mt-12 z-10">
            <h2 className="text-white font-medium text-xl lg:text-4xl w-4/5 lg:w-3/4 lg:pb-20">
              {props.data.design_section[2].title}
            </h2>
            <div className="flex flex-col lg:flex-row justify-between">
              <h3 className="text-white text-base flex-1 pt-12 lg:pt-0">
                {props.data.design_section[2].sub_title}
              </h3>
              <div className="flex flex-col flex-1">
                <p className="text-white text-lg lg:pb-12 pt-14 lg:pt-0">
                  {props.data.design_section[2].body}
                </p>
                <p className="text-white opacity-70 text-base pt-12 lg:pt-0">
                  {props.data.design_section[2].sub_body}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col px-6 lg:px-28 pt-16 lg:pt-48 max-w-7xl lg:pb-52">
          <h2 className="text-white font-medium text-xl lg:text-4xl w-4/5 pb-12 lg:pb-14">
            {props.data.design_section[3].title}
          </h2>
          <div className="flex flex-col lg:flex-row">
            <div className="flex-1 flex flex-col justify-end lg:pr-14">
              <h3 className="text-white text-base pb-16 lg:pt-72">
                {props.data.design_section[3].sub_title}
              </h3>
              <p className="text-white text-lg pb-12">
                {props.data.design_section[3].body}
              </p>
              <div className="-mx-6 lg:-mx-0">
                <Image
                  src={props.data.design_section[3].src_2}
                  alt="MaryAG Clean And Safe"
                  width={488}
                  height={650}
                  layout="responsive"
                  objectFit="cover"
                />
              </div>
            </div>
            <div className="flex-1 lg:pl-14 pt-12 lg:pt-0">
              <div className="-mx-6 lg:-mx-0">
                <Image
                  src={props.data.design_section[3].src_1}
                  alt="MaryAG Clean And Safe"
                  width={590}
                  height={786}
                  layout="responsive"
                  objectFit="cover"
                />
              </div>
              <p className="text-white opacity-70 text-base pt-16 lg:pt-32 w-full lg:w-3/4 mx-auto">
                {props.data.design_section[3].sub_body}
              </p>
            </div>
          </div>
        </div>
      </section>
      <section
        className="bg-grey-700 relative w-full max-w-7xl mx-auto px-6 lg:px-28"
        ref={observeTheAppSection}
      >
        <Element name="theAppAnchor" className="absolute -mt-44"></Element>
        <div className="-mx-6">
          <AppCTAs />
        </div>
        <div className="-mx-6 lg:-mx-0 pt-16 lg:pt-28">
          <Features features={props.data.app_features} type="app" />
        </div>
      </section>
      <section className="bg-grey-700 px-6 lg:px-28 w-full max-w-7xl self-center mx-auto pt-16">
        <ImageGallery {...props} />
      </section>
      <section
        className="bg-grey-700 relative w-full max-w-7xl mx-auto"
        ref={observeSpecsSection}
      >
        <Element name="specsAnchor" className="absolute -mt-44"></Element>
        <div className="px-6 w-full self-center mx-auto pt-8">
          <div className="flex-1">
            <ProductDetails {...props} />
          </div>
        </div>
      </section>
    </Container>
  )
}
